import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  UserPlus,
  Mail,
  Shield,
  Settings,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  MoreHorizontal,
  Plus,
  Users,
  ShieldCheck,
  Key,
  AlertTriangle,
  Copy,
  RefreshCw,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import InvitationDetailsModal from "@/components/InvitationDetailsModal";

interface RbacGroup {
  id: number;
  name: string;
  description: string;
  level: number;
  permissions: string[];
  is_active: boolean;
  is_system?: boolean;
  created_by?: string | null;
  tenant_id?: string | null;
  created_at?: string;
  updated_at?: string;
  configs?: Array<{
    id: number;
    group_id: number;
    config_key: string;
    config_value: any;
    description?: string;
    is_active: boolean;
    is_system_config: boolean;
    created_at: string;
    updated_at: string;
  }>;
}

interface UserAssignment {
  id: number;
  user_id: string;
  links_to: string;
  group_id?: number;
  role: number;
  status: boolean;
  created_at: string;
  updated_at: string;
  metadata?: any;
  group?: RbacGroup;
  child: {
    id: string;
    full_name: string;
    avatar_url: string;
    user_type: string;
  };
}

interface UserInvite {
  id: string;
  email: string;
  group_id: number;
  message: string;
  type: string;
  invited_by: string;
  token: string;
  status: "pending" | "accepted" | "declined" | "expired";
  expires_at: string;
  accepted_at: string | null;
  declined_at: string | null;
  accepted_by: string | null;
  created_at: string;
  updated_at: string;
  group?: RbacGroup;
}

export default function ManageUsers({
  session,
  profile,
}: {
  session: any;
  profile: any;
}) {
  const [users, setUsers] = useState<UserAssignment[]>([]);
  const [rbacGroups, setRbacGroups] = useState<RbacGroup[]>([]);
  const [invites, setInvites] = useState<UserInvite[]>([]);
  const [searchEmail, setSearchEmail] = useState("");
  const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingInvites, setIsLoadingInvites] = useState(false);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserAssignment | null>(null);
  const [inviteMessage, setInviteMessage] = useState("");
  
  // RBAC Group Management State
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [isEditGroupDialogOpen, setIsEditGroupDialogOpen] = useState(false);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [selectedRbacGroup, setSelectedRbacGroup] = useState<RbacGroup | null>(null);
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    level: 0,
    permissions: [] as string[]
  });
  const [configForm, setConfigForm] = useState({
    config_key: '',
    config_value: '',
    description: ''
  });
  const [activeTab, setActiveTab] = useState('users');
  
  // Invitation Modal State
  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false);
  const [selectedInviteForModal, setSelectedInviteForModal] = useState<UserInvite | null>(null);
  
  const { toast } = useToast();

  const API_BASE_URL =
    import.meta.env.VITE_CC_API_URL || "https://api.cause.app";

  // Fetch RBAC groups on component mount (needed for dropdowns)
  useEffect(() => {
    fetchRbacGroups();
  }, []);

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === 'users' && users.length === 0 && !isLoadingUsers) {
      fetchLinkedUsers();
    } else if (activeTab === 'invites' && invites.length === 0 && !isLoadingInvites) {
      fetchPendingInvites();
    }
  }, [activeTab]);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${session?.access_token}`,
    "Content-Type": "application/json",
  });

  const fetchRbacGroups = async () => {
    if (isLoadingGroups) return; // Prevent duplicate calls
    setIsLoadingGroups(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/me/rbac/groups`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setRbacGroups(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching RBAC groups:", error);
    } finally {
      setIsLoadingGroups(false);
    }
  };

  const fetchLinkedUsers = async () => {
    if (isLoadingUsers) return; // Prevent duplicate calls
    setIsLoadingUsers(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/me/rbac/linked-users`,
        {
          headers: getAuthHeaders(),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setUsers(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching linked users:", error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const fetchPendingInvites = async () => {
    if (isLoadingInvites) return; // Prevent duplicate calls
    setIsLoadingInvites(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/me/invites?type=rbac_assignment`,
        {
          headers: getAuthHeaders(),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setInvites(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching pending invites:", error);
    } finally {
      setIsLoadingInvites(false);
    }
  };

  const searchUserByEmail = async (email: string) => {
    if (!email.trim()) {
      toast({
        title: "Error",
        description: "Please enter an email address",
        variant: "destructive",
      });
      return null;
    }

    setIsLoading(true);
    try {
      // This would be an endpoint to search for users by email
      // For now, we'll simulate a user search
      const response = await fetch(
        `${API_BASE_URL}/v1/me/search?email=${encodeURIComponent(email)}`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (response.ok) {
        const data = await response.json();
        return data.data;
      } else {
        toast({
          title: "User not found",
          description: "No user found with this email address",
          variant: "destructive",
        });
        return null;
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to search for user",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const inviteUser = async (
    email: string,
    groupId: number,
    message: string
  ) => {
    setIsLoading(true);
    try {
      // First, we'd create an invite record
      const inviteResponse = await fetch(
        `${API_BASE_URL}/v1/me/invites`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            email,
            group_id: groupId,
            message,
            type: "rbac_assignment",
          }),
        }
      );

      if (inviteResponse.ok) {
        // Send invitation email
        const emailResponse = await fetch(
          `${API_BASE_URL}/v1/me/send-invite`,
          {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({
              email,
              group_id: groupId,
              message,
              invited_by: profile?.full_name || session?.user?.email,
            }),
          }
        );

        if (emailResponse.ok) {
          toast({
            title: "Invitation sent",
            description: `Invitation sent to ${email}`,
          });
          setIsAddDialogOpen(false);
          setSearchEmail("");
          setSelectedGroup(null);
          setInviteMessage("");
          fetchPendingInvites();
        } else {
          throw new Error("Failed to send invitation email");
        }
      } else {
        throw new Error("Failed to create invitation");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send invitation",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const assignUserToGroup = async (userId: string, groupId: number) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/me/rbac/assignments`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            user_id: userId,
            links_to: session?.user?.id,
            group_id: groupId,
            metadata: {
              assigned_via: "invite_acceptance",
            },
          }),
        }
      );

      if (response.ok) {
        toast({
          title: "User assigned",
          description: "User has been successfully assigned to the group",
        });
        fetchLinkedUsers();
      } else {
        throw new Error("Failed to assign user");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to assign user to group",
        variant: "destructive",
      });
    }
  };

  const updateUserAssignment = async (assignmentId: number, updates: any) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/me/rbac/assignments/${assignmentId}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify(updates),
        }
      );

      if (response.ok) {
        toast({
          title: "Assignment updated",
          description: "User assignment has been updated successfully",
        });
        fetchLinkedUsers();
      } else {
        throw new Error("Failed to update assignment");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update user assignment",
        variant: "destructive",
      });
    }
  };

  const removeUser = async (assignmentId: number) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/me/rbac/assignments/${assignmentId}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );

      if (response.ok) {
        toast({
          title: "User removed",
          description: "User has been removed from your organization",
        });
        fetchLinkedUsers();
      } else {
        throw new Error("Failed to remove user");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove user",
        variant: "destructive",
      });
    }
  };

  const handleInviteUser = async () => {
    if (!searchEmail || !selectedGroup) {
      toast({
        title: "Error",
        description: "Please enter an email and select a group",
        variant: "destructive",
      });
      return;
    }

    await inviteUser(searchEmail, selectedGroup, inviteMessage);
  };

  const getStatusBadge = (status: boolean) => {
    return status ? (
      <Badge variant="default" className="bg-green-100 text-green-800">
        <CheckCircle className="w-3 h-3 mr-1" />
        Active
      </Badge>
    ) : (
      <Badge variant="secondary" className="bg-gray-100 text-gray-600">
        <Clock className="w-3 h-3 mr-1" />
        Inactive
      </Badge>
    );
  };

  const getPermissionLevel = (group?: RbacGroup) => {
    if (!group) return "No Group";
    return `Level ${group.level} - ${group.name}`;
  };

  // ============ RBAC Group Management Functions ============

  const createRbacGroup = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/me/rbac/groups`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(groupForm)
      });

      if (response.ok) {
        toast({
          title: "Group created",
          description: "RBAC group has been created successfully",
        });
        setIsGroupDialogOpen(false);
        setGroupForm({ name: '', description: '', level: 0, permissions: [] });
        fetchRbacGroups();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create group');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create group",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateRbacGroup = async () => {
    if (!selectedRbacGroup) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/me/rbac/groups/${selectedRbacGroup.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(groupForm)
      });

      if (response.ok) {
        toast({
          title: "Group updated",
          description: "RBAC group has been updated successfully",
        });
        setIsEditGroupDialogOpen(false);
        setSelectedRbacGroup(null);
        setGroupForm({ name: '', description: '', level: 0, permissions: [] });
        fetchRbacGroups();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update group');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update group",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteRbacGroup = async (groupId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/me/rbac/groups/${groupId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast({
          title: "Group deleted",
          description: "RBAC group has been deleted successfully",
        });
        fetchRbacGroups();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete group');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete group",
        variant: "destructive"
      });
    }
  };

  // ============ Invitation Management Functions ============

  const resendInvitation = async (inviteId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/me/invites/${inviteId}/resend`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast({
          title: "Invitation resent",
          description: "The invitation has been sent again",
        });
        fetchPendingInvites();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to resend invitation');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to resend invitation",
        variant: "destructive"
      });
    }
  };

  const cancelInvitation = async (inviteId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/me/invites/${inviteId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast({
          title: "Invitation cancelled",
          description: "The invitation has been cancelled",
        });
        fetchPendingInvites();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel invitation');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to cancel invitation",
        variant: "destructive"
      });
    }
  };

  const addGroupConfig = async () => {
    if (!selectedRbacGroup) return;
    setIsLoading(true);
    try {
      // Determine JSON value
      let parsedValue;
      try {
        parsedValue = JSON.parse(configForm.config_value);
      } catch {
        parsedValue = configForm.config_value;
      }
      // Upsert: POST if new, PUT if exists
      const exists = selectedRbacGroup.configs?.some(c => c.config_key === configForm.config_key);
      const method = exists ? 'PUT' : 'POST';
      const endpoint = exists
        ? `/v1/me/rbac/groups/${selectedRbacGroup.id}/configs/${configForm.config_key}`
        : `/v1/me/rbac/groups/${selectedRbacGroup.id}/configs`;
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method,
        headers: getAuthHeaders(),
        body: method === 'PUT'
          ? JSON.stringify({ config_value: parsedValue, description: configForm.description })
          : JSON.stringify({ config_key: configForm.config_key, config_value: parsedValue, description: configForm.description })
      });

      if (response.ok) {
        toast({ title: exists ? 'Configuration updated' : 'Configuration added', description: exists ? 'Group configuration updated successfully.' : 'Group configuration added successfully.' });
        setIsConfigDialogOpen(false);
        setConfigForm({ config_key: '', config_value: '', description: '' });
        fetchRbacGroups();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${exists ? 'update' : 'add'} configuration`);
      }
    } catch (error) {
      toast({ title: 'Error', description: error instanceof Error ? error.message : 'Failed to add/update configuration', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const removeGroupConfig = async (groupId: number, configKey: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/me/rbac/groups/${groupId}/configs/${configKey}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast({
          title: "Configuration removed",
          description: "Group configuration has been removed successfully",
        });
        fetchRbacGroups();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove configuration');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to remove configuration",
        variant: "destructive"
      });
    }
  };

  const availablePermissions = ['READ', 'WRITE', 'DELETE', 'ADMIN', 'SUPER_ADMIN'];

  const handlePermissionChange = (permission: string, checked: boolean) => {
    setGroupForm(prev => ({
      ...prev,
      permissions: checked 
        ? [...prev.permissions, permission]
        : prev.permissions.filter(p => p !== permission)
    }));
  };

  const openEditGroup = (group: RbacGroup) => {
    setSelectedRbacGroup(group);
    setGroupForm({
      name: group.name,
      description: group.description || '',
      level: group.level,
      permissions: group.permissions
    });
    setIsEditGroupDialogOpen(true);
  };

  const openAddConfig = (group: RbacGroup) => {
    setSelectedRbacGroup(group);
    setConfigForm({ config_key: '', config_value: '', description: '' });
    setIsConfigDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">User & Permission Management</h1>
          <p className="text-muted-foreground">
            Manage users, permissions, and RBAC groups in your organization
          </p>
        </div>
        <div className="flex space-x-2">
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="w-4 h-4 mr-2" />
                Invite User
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Invite User</DialogTitle>
              <DialogDescription>
                Invite a user to join your organization. They will receive an
                email invitation.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="user@example.com"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="group">RBAC Group</Label>
                <Select
                  value={selectedGroup?.toString()}
                  onValueChange={(value) => setSelectedGroup(parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a group" />
                  </SelectTrigger>
                  <SelectContent>
                    {rbacGroups.map((group) => (
                      <SelectItem key={group.id} value={group.id.toString()}>
                        {group.name} (Level {group.level})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Invitation Message (Optional)</Label>
                <Textarea
                  id="message"
                  placeholder="Add a personal message to the invitation..."
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsAddDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleInviteUser} disabled={isLoading}>
                {isLoading ? "Sending..." : "Send Invitation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="users">
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="invites">
            <Mail className="w-4 h-4 mr-2" />
            Invitations
          </TabsTrigger>
          <TabsTrigger value="groups">
            <ShieldCheck className="w-4 h-4 mr-2" />
            RBAC Groups
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{users.length}</div>
            <p className="text-xs text-muted-foreground">
              Active members in your organization
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Pending Invites
            </CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{invites.length}</div>
            <p className="text-xs text-muted-foreground">
              Awaiting user acceptance
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">RBAC Groups</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{rbacGroups.length}</div>
            <p className="text-xs text-muted-foreground">
              Available permission groups
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle>Organization Members</CardTitle>
          <CardDescription>
            Manage users and their permissions in your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-8">
              <UserPlus className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No users yet</h3>
              <p className="text-muted-foreground mb-4">
                Start by inviting users to join your organization
              </p>
              <Button onClick={() => setIsAddDialogOpen(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Your First User
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-4">
                    <Avatar>
                      <AvatarImage src={user.child.avatar_url} />
                      <AvatarFallback>
                        {user.child.full_name?.charAt(0)?.toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">
                        {user.child.full_name || "Unknown User"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {getPermissionLevel(user.group)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {getStatusBadge(user.status)}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedUser(user);
                            setIsViewDialogOpen(true);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            // Handle edit user
                          }}
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Permissions
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => removeUser(user.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove User
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={selectedUser.child.avatar_url} />
                  <AvatarFallback className="text-lg">
                    {selectedUser.child.full_name?.charAt(0)?.toUpperCase() ||
                      "U"}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-medium">
                    {selectedUser.child.full_name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedUser.child.user_type}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <Label className="text-sm font-medium">RBAC Group</Label>
                  <p className="text-sm">
                    {getPermissionLevel(selectedUser.group)}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Status</Label>
                  <div className="mt-1">
                    {getStatusBadge(selectedUser.status)}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Joined</Label>
                  <p className="text-sm">
                    {new Date(selectedUser.created_at).toLocaleDateString()}
                  </p>
                </div>
                {selectedUser.group && (
                  <div>
                    <Label className="text-sm font-medium">Permissions</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedUser.group.permissions.map((permission) => (
                        <Badge
                          key={permission}
                          variant="outline"
                          className="text-xs"
                        >
                          {permission}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsViewDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </TabsContent>

      {/* Invitations Tab */}
      <TabsContent value="invites" className="space-y-6">
        {/* Invites Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Invites</CardTitle>
              <Mail className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{invites.length}</div>
              <p className="text-xs text-muted-foreground">
                Pending invitations sent
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {invites.filter(i => i.status === 'pending').length}
              </div>
              <p className="text-xs text-muted-foreground">
                Awaiting response
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Expired</CardTitle>
              <XCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {invites.filter(i => i.status === 'expired').length}
              </div>
              <p className="text-xs text-muted-foreground">
                Need to be resent
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Invitations List */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Pending Invitations</CardTitle>
                <CardDescription>
                  Manage sent invitations and their status
                </CardDescription>
              </div>
              <Button onClick={() => setIsAddDialogOpen(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                Send New Invite
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {invites.length === 0 ? (
              <div className="text-center py-8">
                <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No invitations sent</h3>
                <p className="text-muted-foreground mb-4">
                  Start by sending invitations to users to join your organization
                </p>
                <Button onClick={() => setIsAddDialogOpen(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Send Your First Invitation
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {invites.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer hover:border-blue-300 transition-colors group"
                    onClick={() => {
                      setSelectedInviteForModal(invite);
                      setIsInvitationModalOpen(true);
                    }}
                    title="Click to view invitation details"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                        <Mail className="w-5 h-5 text-yellow-600" />
                      </div>
                      <div>
                        <p className="font-medium">{invite.email}</p>
                        <p className="text-sm text-muted-foreground">
                          Invited to {invite.group?.name} • {" "}
                          Sent {new Date(invite.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Expires {new Date(invite.expires_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Eye className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <Badge
                        variant={
                          invite.status === "pending"
                            ? "default"
                            : invite.status === "accepted"
                            ? "default"
                            : invite.status === "declined"
                            ? "destructive"
                            : "secondary"
                        }
                        className={
                          invite.status === "pending"
                            ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                            : invite.status === "accepted"
                            ? "bg-green-100 text-green-800 border-green-300"
                            : invite.status === "declined"
                            ? "bg-red-100 text-red-800 border-red-300"
                            : "bg-gray-100 text-gray-800 border-gray-300"
                        }
                      >
                        {invite.status === "pending" && <Clock className="w-3 h-3 mr-1" />}
                        {invite.status === "accepted" && <CheckCircle className="w-3 h-3 mr-1" />}
                        {invite.status === "declined" && <XCircle className="w-3 h-3 mr-1" />}
                        {invite.status === "expired" && <AlertTriangle className="w-3 h-3 mr-1" />}
                        {invite.status.charAt(0).toUpperCase() + invite.status.slice(1)}
                      </Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedInviteForModal(invite);
                              setIsInvitationModalOpen(true);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(invite.email);
                              toast({
                                title: "Email copied",
                                description: "Email address copied to clipboard",
                              });
                            }}
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Copy Email
                          </DropdownMenuItem>
                          {(invite.status === "pending" || invite.status === "expired") && (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                resendInvitation(invite.id);
                              }}
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Resend Invitation
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              cancelInvitation(invite.id);
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Cancel Invitation
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* RBAC Groups Tab */}
      <TabsContent value="groups" className="space-y-6">
        {/* Group Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Groups</CardTitle>
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{rbacGroups.length}</div>
              <p className="text-xs text-muted-foreground">
                Permission groups configured
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">User Groups</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {rbacGroups.filter(g => !g.is_system).length}
              </div>
              <p className="text-xs text-muted-foreground">
                Custom user groups
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">System Groups</CardTitle>
              <Settings className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {rbacGroups.filter(g => g.is_system).length}
              </div>
              <p className="text-xs text-muted-foreground">
                System default groups
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Groups</CardTitle>
              <Key className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {rbacGroups.filter(g => g.is_active).length}
              </div>
              <p className="text-xs text-muted-foreground">
                Currently active groups
              </p>
            </CardContent>
          </Card>
        </div>

        {/* RBAC Groups Management - Nested Tabs */}
        <Tabs defaultValue="user-groups" className="space-y-6">
          <div className="flex justify-between items-center">
            <TabsList className="grid w-fit grid-cols-2">
              <TabsTrigger value="user-groups" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                User RBAC Groups
              </TabsTrigger>
              <TabsTrigger value="system-defaults" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                System Defaults
              </TabsTrigger>
            </TabsList>
            <Dialog open={isGroupDialogOpen} onOpenChange={setIsGroupDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Group
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create RBAC Group</DialogTitle>
                  <DialogDescription>
                    Create a new permission group with specific access levels
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="group-name">Group Name</Label>
                    <Input
                      id="group-name"
                      placeholder="e.g., Marketing Team"
                      value={groupForm.name}
                      onChange={(e) => setGroupForm(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="group-description">Description</Label>
                    <Textarea
                      id="group-description"
                      placeholder="Brief description of this group's purpose"
                      value={groupForm.description}
                      onChange={(e) => setGroupForm(prev => ({ ...prev, description: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="group-level">Permission Level (1-100)</Label>
                    <Input
                      id="group-level"
                      type="number"
                      min="1"
                      max="100"
                      value={groupForm.level}
                      onChange={(e) => setGroupForm(prev => ({ ...prev, level: parseInt(e.target.value) || 0 }))}
                    />
                    <p className="text-xs text-muted-foreground">
                      Higher numbers = Higher privileges. Must be unique.
                    </p>
                  </div>
                  <div className="space-y-3">
                    <Label>Permissions</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {availablePermissions.map((permission) => (
                        <div key={permission} className="flex items-center space-x-2">
                          <Checkbox
                            id={`perm-${permission}`}
                            checked={groupForm.permissions.includes(permission)}
                            onCheckedChange={(checked) => handlePermissionChange(permission, checked as boolean)}
                          />
                          <Label htmlFor={`perm-${permission}`} className="text-sm">
                            {permission}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsGroupDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={createRbacGroup} disabled={isLoading}>
                    {isLoading ? 'Creating...' : 'Create Group'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* User RBAC Groups Tab */}
          <TabsContent value="user-groups" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Custom User Groups</CardTitle>
                <CardDescription>
                  Manage custom permission groups for your organization users
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  const userGroups = rbacGroups.filter(g => !g.is_system);
                  
                  return userGroups.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">No custom user groups yet</h3>
                      <p className="text-muted-foreground mb-4">
                        Create custom permission groups to organize user access levels
                      </p>
                      <Button onClick={() => setIsGroupDialogOpen(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Your First User Group
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {userGroups.map((group) => (
                        <div
                          key={group.id}
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Users className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <h3 className="font-medium">{group.name}</h3>
                                <p className="text-sm text-muted-foreground">{group.description}</p>
                                <div className="flex items-center space-x-2 mt-2">
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                    Level {group.level}
                                  </Badge>
                                  <div className="flex space-x-1">
                                    {group.permissions.map((permission) => (
                                      <Badge key={permission} variant="secondary" className="text-xs">
                                        {permission}
                                      </Badge>
                                    ))}
                                  </div>
                                  {!group.is_active && (
                                    <Badge variant="destructive">Inactive</Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            {group.configs && group.configs.length > 0 && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-xs font-medium text-muted-foreground mb-2">Configurations:</p>
                                <div className="flex flex-wrap gap-1">
                                  {group.configs.map((config) => (
                                    <Badge key={config.config_key} variant="outline" className="text-xs">
                                      {config.config_key}: {JSON.stringify(config.config_value)}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openAddConfig(group)}
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => openEditGroup(group)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit Group
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openAddConfig(group)}>
                                  <Key className="w-4 h-4 mr-2" />
                                  Add Config
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => deleteRbacGroup(group.id)}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete Group
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* System Defaults Tab */}
          <TabsContent value="system-defaults" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>System Default Groups</CardTitle>
                <CardDescription>
                  Pre-configured system groups with default permissions and settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  const systemGroups = rbacGroups.filter(g => g.is_system);
                  
                  return systemGroups.length === 0 ? (
                    <div className="text-center py-8">
                      <Settings className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">No system groups found</h3>
                      <p className="text-muted-foreground mb-4">
                        System groups will appear here when they are configured by administrators
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {systemGroups.map((group) => (
                        <div key={group.id} className="flex items-center justify-between p-4 border rounded-lg bg-orange-50 border-orange-200">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                                <Settings className="w-5 h-5 text-orange-600" />
                              </div>
                              <div>
                                <div className="flex items-center space-x-2">
                                  <h3 className="font-medium">{group.name}</h3>
                                  <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">
                                    System
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">{group.description}</p>
                                <div className="flex items-center space-x-2 mt-2">
                                  <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                                    Level {group.level}
                                  </Badge>
                                  <div className="flex space-x-1">
                                    {group.permissions.map((permission) => (
                                      <Badge key={permission} variant="secondary" className="text-xs bg-orange-100 text-orange-800">
                                        {permission}
                                      </Badge>
                                    ))}
                                  </div>
                                  {!group.is_active && (
                                    <Badge variant="destructive">Inactive</Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            {group.configs && group.configs.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-orange-200">
                                <p className="text-xs font-medium text-orange-700 mb-2">System Configurations:</p>
                                <div className="flex flex-wrap gap-1">
                                  {group.configs.map((config) => (
                                    <Badge key={config.config_key} variant="outline" className="text-xs bg-orange-100 text-orange-800 border-orange-300">
                                      {config.config_key}: {JSON.stringify(config.config_value)}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openAddConfig(group)}
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>System Group Actions</DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => openEditGroup(group)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit Group
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-yellow-600"
                                  onClick={() => {
                                    if (confirm('Are you sure you want to delete this system group? This action cannot be undone.')) {
                                      deleteRbacGroup(group.id);
                                    }
                                  }}
                                >
                                  <AlertTriangle className="w-4 h-4 mr-2" />
                                  Delete System Group
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </TabsContent>
      </Tabs>

      {/* Edit Group Dialog */}
      <Dialog open={isEditGroupDialogOpen} onOpenChange={setIsEditGroupDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit RBAC Group</DialogTitle>
            <DialogDescription>
              Update the group settings and permissions
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-group-name">Group Name</Label>
              <Input
                id="edit-group-name"
                value={groupForm.name}
                onChange={(e) => setGroupForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-group-description">Description</Label>
              <Textarea
                id="edit-group-description"
                value={groupForm.description}
                onChange={(e) => setGroupForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-group-level">Permission Level</Label>
              <Input
                id="edit-group-level"
                type="number"
                min="1"
                max="100"
                value={groupForm.level}
                onChange={(e) => setGroupForm(prev => ({ ...prev, level: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-3">
              <Label>Permissions</Label>
              <div className="grid grid-cols-2 gap-3">
                {availablePermissions.map((permission) => (
                  <div key={permission} className="flex items-center space-x-2">
                    <Checkbox
                      id={`edit-perm-${permission}`}
                      checked={groupForm.permissions.includes(permission)}
                      onCheckedChange={(checked) => handlePermissionChange(permission, checked as boolean)}
                    />
                    <Label htmlFor={`edit-perm-${permission}`} className="text-sm">
                      {permission}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditGroupDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={updateRbacGroup} disabled={isLoading}>
              {isLoading ? 'Updating...' : 'Update Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Config Dialog */}
      <Dialog open={isConfigDialogOpen} onOpenChange={setIsConfigDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Group Configuration</DialogTitle>
            <DialogDescription>
              Add a configuration setting to {selectedRbacGroup?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Configuration Key</Label>
              <Select
                value={configForm.config_key === '__new__' ? '__new__' : configForm.config_key}
                onValueChange={(value) => {
                  if (value === '__new__') {
                    // new key selected
                    setConfigForm({ config_key: '__new__', config_value: '', description: '' });
                  } else {
                    // existing key selected: load current config
                    const cfg = selectedRbacGroup?.configs?.find(c => c.config_key === value);
                    setConfigForm({
                      config_key: value,
                      config_value: cfg ? JSON.stringify(cfg.config_value) : '',
                      description: cfg?.description || ''
                    });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a key or create new" />
                </SelectTrigger>
                <SelectContent>
                  {selectedRbacGroup?.configs?.map(config => (
                    <SelectItem key={config.config_key} value={config.config_key}>
                      {config.config_key}
                    </SelectItem>
                  ))}
                  <SelectItem value="__new__">Create new key...</SelectItem>
                </SelectContent>
              </Select>
              {configForm.config_key === '__new__' && (
                <div className="mt-2">
                  <Input
                    id="new-config-key"
                    placeholder="Enter new configuration key"
                    value={configForm.config_key === '__new__' ? '' : configForm.config_key}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, config_key: e.target.value }))}
                  />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="config-value">Configuration Value</Label>
              <Input
                id="config-value"
                placeholder="e.g., 10000 or true or ['feature1', 'feature2']"
                value={configForm.config_value}
                onChange={(e) => setConfigForm(prev => ({ ...prev, config_value: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Use JSON format for complex values (arrays, objects, booleans)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="config-description">Description</Label>
              <Input
                id="config-description"
                placeholder="Brief description of this setting"
                value={configForm.description}
                onChange={(e) => setConfigForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfigDialogOpen(false)}>
              Cancel
            </Button>
            {/* change button based on existing config */}
            {(() => {
              const exists = selectedRbacGroup?.configs?.some(c => c.config_key === configForm.config_key);
              return (
                <Button onClick={addGroupConfig} disabled={isLoading}>
                  {isLoading
                    ? (exists ? 'Updating...' : 'Adding...')
                    : (exists ? 'Update Configuration' : 'Add Configuration')}
                </Button>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invitation Details Modal */}
      {selectedInviteForModal && (
        <InvitationDetailsModal
          isOpen={isInvitationModalOpen}
          onClose={() => {
            setIsInvitationModalOpen(false);
            setSelectedInviteForModal(null);
          }}
          inviteId={selectedInviteForModal.id}
          token={selectedInviteForModal.token}
          onInvitationAccepted={() => {
            fetchPendingInvites();
            fetchLinkedUsers();
          }}
          onInvitationDeclined={() => {
            fetchPendingInvites();
          }}
        />
      )}
    </div>
  );
}
